/**
 * Typed fetch wrapper。
 *
 * 職責：
 *  - 自動帶 Authorization（依路徑選 session / tenant token）+ X-Request-Id
 *  - 解析後端統一錯誤格式 {error:{code,message,details}} → 丟出 ApiError
 *  - 偵測 token 失效（曾帶 token 卻 401）→ 登出並導回登入頁
 *  - 處理 204 無 body、302 redirect（附件下載）
 */

import type { ErrorCode } from '@bom/shared';
import { sessionStore } from '../stores/sessionStore';

const BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  readonly code: ErrorCode | string;
  readonly status: number;
  readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type AuthMode = 'session' | 'tenant' | 'none';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** 強制指定用哪個 token；預設 'auto'：/auth、/me 用 session，其餘優先 tenant 再 fallback session */
  auth?: AuthMode | 'auto';
  query?: Record<string, string | number | undefined>;
}

function pickToken(
  path: string,
  mode: AuthMode | 'auto',
): { token: string | null; mode: AuthMode } {
  const s = sessionStore.getState();
  if (mode === 'none') return { token: null, mode: 'none' };
  if (mode === 'session') return { token: s.sessionJwt, mode: 'session' };
  if (mode === 'tenant') return { token: s.tenantJwt, mode: 'tenant' };
  // auto
  const isAuthPath = path.startsWith('/auth') || path === '/me' || path.startsWith('/me/');
  if (isAuthPath) return { token: s.sessionJwt, mode: 'session' };
  return { token: s.tenantJwt ?? s.sessionJwt, mode: 'tenant' };
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(BASE_URL + path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

function onTokenExpired(): void {
  sessionStore.getState().logout();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login?expired=1');
  }
}

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = 'auto', query } = opts;
  const { token } = pickToken(path, auth);

  const headers: Record<string, string> = {
    'X-Request-Id': crypto.randomUUID(),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(buildUrl(path, query), init);

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let message = res.statusText || 'Request failed';
    let details: unknown;
    try {
      const parsed = (await res.json()) as {
        error?: { code?: string; message?: string; details?: unknown };
      };
      if (parsed?.error) {
        code = parsed.error.code ?? code;
        message = parsed.error.message ?? message;
        details = parsed.error.details;
      }
    } catch {
      /* 非 JSON body，保留預設 */
    }
    // 曾帶 token 卻 401 → 視為 token 失效（非登入失敗）
    if (res.status === 401 && token) onTokenExpired();
    throw new ApiError(res.status, code, message, details);
  }

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body }),
  del: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};
